import type {
	EntityId,
	IChartingLibraryWidget,
} from "@public/charting_library";
import { type RefObject, useEffect, useRef } from "react";
import { toNumber } from "#/lib/formatters";
import {
	TRADE_GREEN_HEX,
	TRADE_LIQUIDATION,
	TRADE_ORDER_GREEN,
	TRADE_ORDER_RED,
	TRADE_RED_HEX,
} from "../lib/trade-colors.ts";
import {
	useTradeAccount,
	useTradeMarket,
	useTradeOpenOrders,
} from "./use-trade-data";

type ActiveChart = ReturnType<IChartingLibraryWidget["activeChart"]>;

type TradeLineOptions = {
	price: number;
	label: string;
	color: string;
	linestyle: number; // 0 = solid, 2 = dashed
	fontsize: number;
	bold: boolean;
};

function createTradeLine(chart: ActiveChart, opts: TradeLineOptions) {
	return chart.createShape(
		{ price: opts.price, time: 0 as never },
		{
			shape: "horizontal_line",
			text: opts.label,
			lock: true,
			disableSelection: true,
			disableSave: true,
			disableUndo: true,
			showInObjectsTree: false,
			zOrder: "top",
			overrides: {
				linecolor: opts.color,
				linestyle: opts.linestyle,
				linewidth: 1,
				showPrice: true,
				textcolor: opts.color,
				fontsize: opts.fontsize,
				bold: opts.bold,
				horzLabelsAlign: "left",
				vertLabelsAlign: "bottom",
			},
		},
	);
}

function removeTrackedShapes(
	widget: IChartingLibraryWidget,
	shapeIds: EntityId[],
) {
	if (shapeIds.length === 0) return;

	let chart: ActiveChart;
	try {
		chart = widget.activeChart();
	} catch {
		// Widget already torn down — its shapes go with it; nothing to do.
		return;
	}

	for (const id of shapeIds) {
		try {
			chart.removeEntity(id);
		} catch {
			// shape may already be gone
		}
	}
}

const TRADE_OVERLAY_COLORS: ReadonlySet<string> = new Set([
	TRADE_GREEN_HEX,
	TRADE_RED_HEX,
	TRADE_ORDER_GREEN,
	TRADE_ORDER_RED,
	TRADE_LIQUIDATION,
]);

function removeRestoredTransientShapes(widget: IChartingLibraryWidget) {
	let chart: ActiveChart;

	try {
		chart = widget.activeChart();
	} catch {
		return;
	}

	for (const { id } of chart.getAllShapes()) {
		try {
			const shape = chart.getShapeById(id);

			const isFlaggedTransient =
				!shape.isSavingEnabled() &&
				!shape.isSelectionEnabled() &&
				!shape.isShowInObjectsTreeEnabled();

			if (isFlaggedTransient) {
				chart.removeEntity(id);
				continue;
			}

			// Catch shapes restored from localStorage whose flags may have
			// changed during save/restore. Our trade overlays use a fixed set
			// of colors that users would never pick manually.
			const props = shape.getProperties();
			if (
				typeof props.linecolor === "string" &&
				TRADE_OVERLAY_COLORS.has(props.linecolor)
			) {
				chart.removeEntity(id);
			}
		} catch {
			// ignore shapes that disappear during cleanup
		}
	}
}

function reinforceTransientShapeFlags(
	widget: IChartingLibraryWidget,
	id: EntityId,
) {
	try {
		const shape = widget.activeChart().getShapeById(id);
		shape.setSavingEnabled(false);
		shape.setSelectionEnabled(false);
		shape.setShowInObjectsTreeEnabled(false);
		shape.setUserEditEnabled(false);
	} catch {
		// ignore shapes that are removed before configuration completes
	}
}

const DEBOUNCE_MS = 100;

export function useChartTradeLines(
	widgetRef: RefObject<IChartingLibraryWidget | null>,
	isReady: boolean,
) {
	const { market } = useTradeMarket();
	const { positions } = useTradeAccount();
	const { openOrders } = useTradeOpenOrders();

	const shapeIdsRef = useRef<EntityId[]>([]);

	useEffect(() => {
		const widget = widgetRef.current;
		if (!widget || !isReady) return;

		let disposed = false;

		const timer = setTimeout(() => {
			if (disposed) return;

			let chart: ActiveChart;
			try {
				chart = widget.activeChart();
			} catch {
				return;
			}

			removeTrackedShapes(widget, shapeIdsRef.current);
			shapeIdsRef.current = [];
			removeRestoredTransientShapes(widget);

			const ids: EntityId[] = [];

			const trackShape = (promise: Promise<EntityId>) => {
				promise
					.then((id) => {
						if (disposed || widgetRef.current !== widget) {
							try {
								widget.activeChart().removeEntity(id);
							} catch {
								// chart may already be torn down
							}
							return;
						}

						reinforceTransientShapeFlags(widget, id);
						ids.push(id);
						shapeIdsRef.current = ids;
					})
					.catch(() => {});
			};

			// --- Position lines ---
			const currentPositions = positions.filter(
				(p) => p.coin === market.coin && toNumber(p.szi) !== 0,
			);

			for (const pos of currentPositions) {
				const szi = toNumber(pos.szi);
				const isLong = szi > 0;
				const pnl = toNumber(pos.unrealizedPnl);
				const pnlSign = pnl >= 0 ? "+" : "";
				const direction = isLong ? "LONG" : "SHORT";
				const label = `${direction} ${Math.abs(szi)} ${pos.coin}  PNL ${pnlSign}$${Math.abs(pnl).toFixed(2)}`;

				trackShape(
					createTradeLine(chart, {
						price: toNumber(pos.entryPx),
						label,
						color: isLong ? TRADE_GREEN_HEX : TRADE_RED_HEX,
						linestyle: 0,
						fontsize: 11,
						bold: true,
					}),
				);

				const liquidationPx = toNumber(pos.liquidationPx);
				if (liquidationPx > 0) {
					trackShape(
						createTradeLine(chart, {
							price: liquidationPx,
							label: `LIQ ${pos.coin}`,
							color: TRADE_LIQUIDATION,
							linestyle: 1, // dotted
							fontsize: 10,
							bold: false,
						}),
					);
				}
			}

			// --- Order lines ---
			const currentOrders = openOrders.filter(
				(o) => o.coin === market.coin,
			);

			for (const order of currentOrders) {
				const isBuy = order.side === "B";
				const sideLabel = isBuy ? "BUY" : "SELL";
				const label = `${order.orderType ?? "Limit"} ${sideLabel} ${order.sz} ${order.coin}`;

				trackShape(
					createTradeLine(chart, {
						price: toNumber(order.limitPx),
						label,
						color: isBuy ? TRADE_ORDER_GREEN : TRADE_ORDER_RED,
						linestyle: 2,
						fontsize: 10,
						bold: false,
					}),
				);
			}
		}, DEBOUNCE_MS);

		return () => {
			disposed = true;
			clearTimeout(timer);
		};
	}, [isReady, positions, openOrders, market.coin]);
}
